<template>
  <div>
    <Modal modalId="EditAccountInfoModal">
      <ModalHeader :title="$t('xEditAccountInformation')" icon="fas fa-user-circle">
        <button
          class="btn btn-icon-button btn-icon-button--gray"
          @click="resetModal(), closeModal('EditAccountInfoModal')"
          data-test-hook="x-button-edit-account-information-modal"
        >
          <i class="fas fa-times" />
        </button>
      </ModalHeader>
      <ModalBody>
        <b-alert :show="shouldShowAddressUpdateBanner" class="alert alert-success">{{
          $t('xEditAccountAddressSuccessful')
        }}</b-alert>
        <div v-if="accountData">
          <div class="row">
            <div class="col">
              <div class="form-group" id="firstName-group">
                <b-form-input
                  class="form-control"
                  id="firstName"
                  v-model="$v.accountData.firstName.$model"
                  type="text"
                  :placeholder="$t('xFirstName')"
                  :state="
                    validState($v.accountData.firstName) == null || validState($v.accountData.firstName) == true
                      ? null
                      : false
                  "
                  aria-describedby="firstName-invalid"
                />
                <div class="invalid-feedback" id="firstName-invalid">
                  {{ $t('xRequiredField') }}
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="form-group" id="lastName-group">
                <b-form-input
                  class="form-control"
                  id="lastName"
                  v-model="$v.accountData.lastName.$model"
                  type="text"
                  :placeholder="$t('xLastName')"
                  :state="
                    validState($v.accountData.lastName) == null || validState($v.accountData.lastName) == true
                      ? null
                      : false
                  "
                  aria-describedby="lastName-invalid"
                />
                <div class="invalid-feedback" id="lastName-invalid">
                  {{ $t('xRequiredField') }}
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="form-group" id="phone-group">
                <input
                  class="form-control"
                  id="phone"
                  :placeholder="$t('xPhoneNumber')"
                  v-model="$v.phoneNumber.$model"
                  type="text"
                  maxlength="12"
                  :state="validState($v.phoneNumber)"
                  :class="status($v.phoneNumber)"
                  aria-describedby="phoneNumber-invalid"
                />
                <div class="invalid-feedback" id="phoneNumber-invalid">
                  {{ $t('xPleaseEnterAValidPhoneNumber') }}
                </div>
              </div>
            </div>
          </div>
          <div class="row" v-if="!editingAddress">
            <div class="col">
              <p>{{ accountProfile.address.line1 }}</p>
              <p v-if="accountProfile.address.line2">{{ accountProfile.address.line2 }}</p>
              <p>
                {{
                  accountProfile.address.city +
                    ', ' +
                    accountProfile.address.region +
                    ' ' +
                    accountProfile.address.postalCode
                }}
              </p>
              <p>{{ accountProfile.address.country }}</p>
            </div>
            <div class="col-auto" v-if="!editingAddress">
              <button
                class="btn btn-icon-button"
                @click="openAddressSearchModal"
                data-test-hook="pen-button-edit-account-modal"
              >
                <i class="fas fa-pencil" />
              </button>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          class="btn btn-outline-warning"
          @click="resetModal(), closeModal('EditAccountInfoModal')"
          data-test-hook="cancel-button-edit-account-information-modal"
        >
          {{ $t('xCancel') }}
        </button>
        <button
          class="btn btn-warning"
          @click="save()"
          :disabled="saving"
          data-test-hook="save-button-edit-account-information-modal"
        >
          {{ $t('xSave') }}
          <div class="spinner-border text-muted ml-2" label="Spinning" v-if="saving"></div>
        </button>
      </ModalFooter>
    </Modal>
    <AddressSearchModals
      ref="addressSearchModalsRef"
      @onSubmit="saveAddress"
      @onClose="openModal('EditAccountInfoModal')"
      :addressFormTitle="$t('xEditAccountAddress')"
      :isBillingAddress="hasActiveSubscription"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { required, minLength, maxLength, numeric } from 'vuelidate/lib/validators'
import AddressSearchModals from '../Common/Address/AddressSearchModals.vue'
import { openModal } from '../../utils/mixins'

export default {
  components: { AddressSearchModals },
  props: ['shouldSetModal', 'hasActiveSubscription'],
  data() {
    return {
      accountData: null,
      editingAddress: false,
      saving: false,
      phoneNumber: null,
      shouldShowAddressUpdateBanner: false
    }
  },
  validations: {
    phoneNumber: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(12),
      numeric,
      startsWithZero(phoneNumber) {
        const regex = /^[1-9]{1}[0-9]{9,11}$/
        return regex.test(phoneNumber)
      }
    },
    accountData: {
      firstName: {
        required,
        minLength: 1
      },
      lastName: {
        required,
        minLength: 1
      }
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      accountInfo: state => state.account.accountInfo,
      apparatuses: state => state.apparatus.apparatuses,
      user: state => state.account.user,
      isImpersonating: state => state.auth.isImpersonating
    })
  },
  methods: {
    openAddressSearchModal() {
      this.closeModal('EditAccountInfoModal')
      this.$refs.addressSearchModalsRef.openAddressFormModal()
    },
    setModal() {
      this.accountData = _.cloneDeep(this.accountProfile)
      if (this.accountInfo.ownerPhoneNumber) {
        this.phoneNumber = _.cloneDeep(this.accountInfo.ownerPhoneNumber)
      }
    },
    resetModal() {
      this.accountData = null
      this.saving = false
      this.editingAddress = false
      this.phoneNumber = null
      this.shouldShowAddressUpdateBanner = false
      this.$emit('shouldSetModal')
    },
    async saveAddress(newAddress, responseOptions) {
      if (newAddress) {
        const requestBody = {
          organizationAddress: {
            type: newAddress.addressType,
            line1: newAddress.address1,
            line2: newAddress.address2,
            city: newAddress.city,
            region: newAddress.state,
            country: newAddress.countryCode,
            postalCode: newAddress.postalCode,
            organizationId: this.accountData.organizationId
          }
        }
        this.$store
          .dispatch('updateAccountHolder', requestBody)
          .then(async result => {
            if (result) {
              this.shouldShowAddressUpdateBanner = true
              await this.$store.dispatch('getAccountProfile')
              await this.$store.dispatch('getAccountInfo')
              this.setModal()
              responseOptions.closeAddressModals()
              this.openModal('EditAccountInfoModal')
            } else {
              responseOptions.openAddressFormWithError()
            }
          })
          .catch(() => {
            responseOptions.openAddressFormWithError()
          })
      }
    },
    isAccountDataValid() {
      return !!(this.accountData.firstName && this.accountData.lastName && this.phoneNumber)
    },
    async save() {
      this.saving = true
      let updateResult = true
      // if any issues, immediately abort
      if (!this.isAccountDataValid()) {
        this.saving = false
        return
      } else {
        // if name is different, save it
        if (
          this.accountData.firstName !== this.accountProfile.firstName ||
          this.accountData.lastName !== this.accountProfile.lastName
        ) {
          updateResult = await this.saveAccountHolder()
        }
        // if phone number is different, save it (different endpoint)
        if (updateResult && this.phoneNumber !== this.accountInfo.ownerPhoneNumber) {
          updateResult = await this.savePrimary()
        }

        // if any issues, show toast and keep modal open
        if (!updateResult) {
          this.saving = false
          this.$store.dispatch('displayToast', {
            title: this.$t('xError'),
            message: this.$t('xUpdatingYourInformationFailedPleaseTryAgain'),
            variant: 'danger'
          })
          return
        }

        this.saving = false
        this.resetModal()
        this.closeModal('EditAccountInfoModal')
        return
      }
    },
    async saveAccountHolder() {
      let result = false
      await this.$store
        .dispatch('updateAccountHolder', {
          firstName: this.accountData.firstName,
          lastName: this.accountData.lastName,
          // address has to be included in this request because reasons, but is not actually changing here (it saves in the address modal flow)
          organizationAddress: {
            ...this.accountProfile.address,
            organizationId: this.accountProfile.organizationId
          }
        })
        .then(response => {
          if (!this.isImpersonating) this.$store.dispatch('getUser')
          else this.$store.dispatch('getImpersonatedUser')
          this.$store.dispatch('getAccountProfile')
          this.$store.dispatch('getAccountInfo')
          result = true
        })
        .catch(() => {
          result = false
        })
      return result
    },
    async savePrimary() {
      let result = false
      if (this.apparatuses == null || this.apparatuses.length == 0) {
        await this.$store.dispatch('getApparatuses')
      }
      let currentPreferences
      let contactInfo
      if (this.apparatuses != null && this.apparatuses.length != 0) {
        let apparatusId = this.apparatuses[0].apparatusId
        await this.$store
          .dispatch('getApparatusPreferencesById', apparatusId)
          .then(response => {
            currentPreferences = response.data
          })
          .catch(error => {
            this.saving = false
            return result
          })
          .finally(() => {})

        if (!currentPreferences) {
          return result
        }
        contactInfo = _.cloneDeep(currentPreferences)
      } else {
        contactInfo = { phones: [], emails: [] }
      }
      const newPrimaryPhone = [this.phoneNumber]
      await this.$store
        .dispatch('updateContactInfo', {
          phones: newPrimaryPhone,
          emails: contactInfo.emails,
          changePrimary: true
        })
        .then(response => {
          currentPreferences = contactInfo
          if (!this.isImpersonating) this.$store.dispatch('getUser')
          else this.$store.dispatch('getImpersonatedUser')
          this.$store.dispatch('getAccountInfo')
          result = true
        })
        .catch(_ => false)
      return result
    }
  },
  watch: {
    shouldSetModal: function(newVal, oldVal) {
      if (newVal) {
        this.setModal()
      }
    }
  }
}
</script>

<style scoped>
.editBottom {
  bottom: 0;
}
</style>
